import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Referral } from '@principle-theorem/principle-core';
import {
  IReferral,
  ReferralStatus,
  ReferralType,
  StafferActor,
} from '@principle-theorem/principle-core/interfaces';
import { Timestamp, WithRef } from '@principle-theorem/shared';
import { Observable } from 'rxjs';

export interface IReferralListState {
  referrals: WithRef<IReferral>[];
  loading: boolean;
  error: string | undefined;
  filters: IReferralFilters;
  currentStaffer: StafferActor | undefined;
}

export interface IReferralFilters {
  from: Timestamp | undefined;
  to: Timestamp | undefined;
  status: ReferralStatus[];
  type: ReferralType[];
  searchTerm: string;
  needsActionOnly: boolean;
}

const initialState: IReferralListState = {
  referrals: [],
  loading: false,
  error: undefined,
  filters: {
    from: undefined,
    to: undefined,
    status: [],
    type: [],
    searchTerm: '',
    needsActionOnly: false,
  },
  currentStaffer: undefined,
};

@Injectable()
export class ReferralListStore extends ComponentStore<IReferralListState> {
  readonly referrals$ = this.select((state) => state.referrals);
  readonly loading$ = this.select((state) => state.loading);
  readonly filters$ = this.select((state) => state.filters);
  readonly searchTerm$ = this.select((state) => state.filters.searchTerm);
  readonly needsActionOnly$ = this.select(
    (state) => state.filters.needsActionOnly
  );
  readonly currentStaffer$ = this.select((state) => state.currentStaffer);

  readonly needsActionCount$ = this.select(
    this.referrals$,
    this.currentStaffer$,
    (referrals, staffer) => {
      if (!staffer) {
        return 0;
      }
      return referrals.filter((referral) =>
        Referral.hasUnreadChanges(referral, staffer)
      ).length;
    }
  );

  readonly filteredReferrals$ = this.select(
    this.referrals$,
    this.filters$,
    this.currentStaffer$,
    (referrals, filters, staffer) => {
      return referrals.filter((referral) => {
        return (
          this._matchesSearchTerm(referral, filters.searchTerm) &&
          this._matchesStatus(referral, filters.status) &&
          this._matchesType(referral, filters.type) &&
          this._matchesDateRange(referral, filters.from, filters.to) &&
          this._matchesNeedsAction(referral, filters.needsActionOnly, staffer)
        );
      });
    }
  );

  // UPDATERS
  readonly setReferrals = this.updater(
    (state, referrals: WithRef<IReferral>[]) => ({
      ...state,
      referrals,
    })
  );

  readonly removeReferral = this.updater(
    (state, referral: WithRef<IReferral>) => ({
      ...state,
      referrals: state.referrals.filter(
        (item) => item.ref.id !== referral.ref.id
      ),
    })
  );

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: loading ? undefined : state.error,
  }));

  readonly updateFilters = this.updater(
    (state, filters: Partial<IReferralListState['filters']>) => ({
      ...state,
      filters: {
        ...state.filters,
        ...filters,
      },
    })
  );

  readonly resetFilters = this.updater((state) => ({
    ...state,
    filters: initialState.filters,
  }));

  readonly setCurrentStaffer = this.updater(
    (state, staffer: StafferActor | undefined) => ({
      ...state,
      currentStaffer: staffer,
    })
  );

  readonly setNeedsActionOnly = this.updater(
    (state, needsActionOnly: boolean) => ({
      ...state,
      filters: {
        ...state.filters,
        needsActionOnly,
      },
    })
  );

  // EFFECTS
  readonly loadReferrals = this.effect(
    (referrals$: Observable<WithRef<IReferral>[]>) =>
      referrals$.pipe(
        tapResponse(
          (referrals) => this.patchState({ referrals }),
          (error) => {
            // eslint-disable-next-line no-console
            console.error('Error loading referrals:', error);
          }
        )
      )
  );

  constructor() {
    super(initialState);
  }

  getReferral$(uid: string): Observable<WithRef<IReferral> | undefined> {
    return this.select((state) =>
      state.referrals.find((referral) => referral.uid === uid)
    );
  }

  private _matchesSearchTerm(
    referral: WithRef<IReferral>,
    searchTerm: string
  ): boolean {
    if (!searchTerm) {
      return true;
    }

    const searchLower = searchTerm.toLowerCase();
    const terms = [
      referral.patient?.name,
      referral.referrer?.name,
      referral.recipient?.name,
      referral.uid,
    ];

    return terms.some(
      (term) => term && term.toString().toLowerCase().includes(searchLower)
    );
  }

  private _matchesStatus(
    referral: WithRef<IReferral>,
    statusFilters: ReferralStatus[]
  ): boolean {
    if (!statusFilters || statusFilters.length === 0) {
      return true;
    }

    if (!referral.status) {
      return false;
    }

    return statusFilters.includes(referral.status);
  }

  private _matchesType(
    referral: WithRef<IReferral>,
    typeFilters: ReferralType[]
  ): boolean {
    if (!typeFilters || typeFilters.length === 0) {
      return true;
    }
    if (!referral.type) {
      return false;
    }

    return typeFilters.includes(referral.type);
  }

  private _matchesDateRange(
    referral: WithRef<IReferral>,
    from: Timestamp | undefined,
    to: Timestamp | undefined
  ): boolean {
    if (!from && !to) {
      return true;
    }

    const referralDate = referral.createdAt;
    const afterFrom = !from || referralDate >= from;
    const beforeTo = !to || referralDate <= to;
    return afterFrom && beforeTo;
  }

  private _matchesNeedsAction(
    referral: WithRef<IReferral>,
    needsActionOnly: boolean,
    staffer: StafferActor | undefined
  ): boolean {
    if (!needsActionOnly) {
      return true;
    }
    if (!staffer) {
      return true;
    }
    return Referral.hasUnreadChanges(referral, staffer);
  }
}
